import React, { useEffect, useState, useCallback } from 'react';
import { Zap, Monitor, Download, Check, X, ShieldCheck } from 'lucide-react';
import { useScreenWakeLock } from '../utils/useScreenWakeLock';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';

const LS_KEY = 'booth_always_on';
const LS_SEEN_KEY = 'booth_always_on_seen';

/**
 * Always-On / Kiosk mode for the venue tablet.
 *
 * YouTube pauses an embedded player the moment the browser tab is hidden
 * (Chrome-on-Android, server-enforced) — so the only reliable way to keep a
 * venue booth playing continuously is to NEVER let it background. This control
 * does exactly that for a dedicated tablet:
 *   • holds a screen Wake Lock so the display never auto-locks,
 *   • puts the booth into fullscreen (no tabs/address bar to wander off to),
 *   • offers "Install app" (PWA → standalone window) when available,
 *   • shows the one-time OS setup steps (battery optimisation, screen pinning).
 */
export default function AlwaysOnMode({ compact = false }) {
  const [enabled, setEnabled] = useState(() => {
    try { return localStorage.getItem(LS_KEY) === '1'; } catch { return false; }
  });
  const [open, setOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);

  useScreenWakeLock(enabled);

  useEffect(() => {
    const onBIP = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', onBIP);
    try {
      setIsStandalone(
        window.matchMedia?.('(display-mode: standalone)')?.matches
        || window.navigator.standalone === true,
      );
    } catch { /* noop */ }
    return () => window.removeEventListener('beforeinstallprompt', onBIP);
  }, []);

  // First-run only: surface the one-time tablet setup (Screen timeout → Never
  // is the definitive fix for the "screen locks and music pauses" issue). We
  // never auto-open again once the operator has seen it.
  useEffect(() => {
    try {
      if (localStorage.getItem(LS_SEEN_KEY) !== '1') {
        setOpen(true);
        localStorage.setItem(LS_SEEN_KEY, '1');
      }
    } catch { /* noop */ }
  }, []);

  const enterFullscreen = useCallback(async () => {
    try {
      const el = document.documentElement;
      if (el.requestFullscreen && !document.fullscreenElement) await el.requestFullscreen();
    } catch { /* fullscreen is best-effort */ }
  }, []);

  const exitFullscreen = useCallback(async () => {
    try { if (document.fullscreenElement) await document.exitFullscreen(); } catch { /* noop */ }
  }, []);

  const toggle = useCallback(async () => {
    const next = !enabled;
    setEnabled(next);
    try { localStorage.setItem(LS_KEY, next ? '1' : '0'); } catch { /* noop */ }
    if (next) { await enterFullscreen(); setOpen(true); } else { await exitFullscreen(); }
  }, [enabled, enterFullscreen, exitFullscreen]);

  const install = useCallback(async () => {
    if (!deferredPrompt) return;
    try { deferredPrompt.prompt(); await deferredPrompt.userChoice; } catch { /* noop */ }
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  return (
    <>
      <button
        type="button"
        onClick={toggle}
        onContextMenu={(e) => { e.preventDefault(); setOpen(true); }}
        title="Always-On mode — keep the booth playing on a dedicated tablet"
        data-testid="always-on-toggle"
        aria-pressed={enabled}
        className={compact ? [
          'w-11 h-11 flex-shrink-0 rounded-full flex items-center justify-center',
          'transition-[transform,box-shadow,background-color] active:scale-95',
          enabled
            ? 'bg-fuchsia-500 text-white shadow-[0_0_20px_rgba(217,70,239,0.55)]'
            : 'bg-zinc-800 text-zinc-300 border border-zinc-700',
        ].join(' ') : [
          'h-14 px-5 rounded-full flex items-center gap-2 font-bold flex-shrink-0',
          'transition-[transform,box-shadow,background-color] active:scale-95',
          enabled
            ? 'bg-fuchsia-500 text-white shadow-[0_0_30px_rgba(217,70,239,0.55)] hover:shadow-[0_0_40px_rgba(217,70,239,0.85)]'
            : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700 border border-zinc-700',
        ].join(' ')}
      >
        <Zap className={`w-5 h-5 ${enabled ? 'animate-pulse' : ''}`} />
        {!compact && (
          <span className="tracking-wider hidden sm:inline" style={{ fontFamily: 'Outfit' }}>
            ALWAYS-ON
          </span>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md" data-testid="always-on-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Zap className="w-5 h-5 text-fuchsia-400" />
              Always-On Mode {enabled ? 'is ON' : 'is OFF'}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Keeps the booth playing continuously on a dedicated venue tablet by
              never letting it go to the background.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-3 rounded-lg bg-zinc-900 border border-zinc-800 p-3" data-testid="always-on-status">
              <ShieldCheck className={`w-5 h-5 flex-shrink-0 ${enabled ? 'text-emerald-400' : 'text-zinc-600'}`} />
              <div>
                <p className="font-semibold text-zinc-200">
                  {enabled ? 'Screen stays awake + fullscreen' : 'Screen is kept awake on the booth'}
                </p>
                <p className="text-zinc-500 text-xs">
                  {enabled
                    ? 'The display won’t auto-lock and the booth runs fullscreen while this tab stays in front.'
                    : 'The booth already holds the screen awake while it’s open. Tap Always-On to also run fullscreen + hide the browser bars.'}
                </p>
              </div>
            </div>

            {!isStandalone && deferredPrompt && (
              <button
                type="button"
                onClick={install}
                data-testid="always-on-install"
                className="w-full flex items-center justify-center gap-2 h-11 rounded-lg bg-fuchsia-500 hover:bg-fuchsia-400 text-white font-semibold"
              >
                <Download className="w-4 h-4" /> Install booth as an app
              </button>
            )}
            {isStandalone && (
              <p className="flex items-center gap-2 text-xs text-emerald-400">
                <Check className="w-4 h-4" /> Running as an installed app — perfect for kiosk use.
              </p>
            )}

            <div className="rounded-lg bg-zinc-900 border border-zinc-800 p-3">
              <p className="flex items-center gap-2 font-semibold text-zinc-200 mb-2">
                <Monitor className="w-4 h-4 text-cyan-400" /> One-time tablet setup
              </p>
              <ol className="list-decimal list-inside space-y-1.5 text-zinc-400 text-xs">
                <li>
                  <b className="text-emerald-400">Most important —</b> Android Settings → Display →
                  <b> Screen timeout → Never</b>. This alone stops the screen locking on its own
                  and keeps the music playing.
                </li>
                <li>Settings → Apps → your browser → Battery → <b>Unrestricted</b> (no battery optimisation).</li>
                <li>Install the booth as an app (button above) or “Add to Home screen”, then enable <b>Screen pinning</b> and pin it.</li>
                <li>Keep the tablet <b>plugged in</b> and leave this booth in the foreground.</li>
              </ol>
              <p className="text-[11px] text-zinc-600 mt-2">
                Note: pressing the tablet’s <b>power button</b> turns the screen off at the device
                level — no website can block that, and YouTube pauses while the screen is off (it
                resumes the instant you wake the screen). With “Screen timeout → Never” set, just
                don’t press power and the booth keeps playing non-stop.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setOpen(false)}
            data-testid="always-on-close"
            className="mt-1 w-full h-10 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 font-semibold flex items-center justify-center gap-2"
          >
            <X className="w-4 h-4" /> Done
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
}
